/**
 * Preprocess markdown text to fix broken table rows caused by \n inside table cells.
 *
 * When the AI response contains literal \n (escaped newline) within a markdown table cell,
 * replacing it with an actual newline splits the table row into multiple lines.
 * The markdown parser then fails to recognize it as a table row, causing content to "leak"
 * outside the table.
 *
 * This function:
 * 1. Replaces escaped \n with actual newlines
 * 2. Detects and merges broken table rows back into single lines
 */
export function preprocessMarkdown(text: string): string {
  // Step 1: Replace escaped \n with actual newlines
  let processed = text.replace(/\\n/g, '\n');

  // Step 2: Fix broken table rows
  // A valid markdown table row starts with | and ends with |.
  // If a line starts with | but doesn't end with |, the next non-| non-empty line
  // is likely a continuation of the same row (broken by \n in cell content).
  const lines = processed.split('\n');
  const merged: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const currentTrimmed = lines[i].trim();

    if (merged.length > 0) {
      const lastIdx = merged.length - 1;
      const lastTrimmed = merged[lastIdx].trim();

      // Check if previous line is an incomplete table row
      // (starts with | but doesn't end with |, and is not a separator row like |---|---|)
      if (
        lastTrimmed.startsWith('|') &&
        !lastTrimmed.endsWith('|') &&
        !/^\|[\s\-:|]+$/.test(lastTrimmed)
      ) {
        // Merge if current line doesn't start a new table row and isn't empty
        if (currentTrimmed !== '' && !currentTrimmed.startsWith('|')) {
          merged[lastIdx] = merged[lastIdx] + ' ' + currentTrimmed;
          continue;
        }
      }
    }

    merged.push(lines[i]);
  }

  return merged.join('\n');
}
